import LockedBadge from './LockedBadge';
import React from 'react';
import { Session } from '../services/api';
import LocationMap from './LocationMap';

function humanizeVertical(slug: string): string {
  const s = slug.startsWith('x_') ? slug.slice(2) : slug;
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function humanizeRiskLevel(riskLevel: string): string {
  switch (riskLevel) {
    case 'high_confidence_human': return 'High confidence human';
    case 'normal': return 'Likely human';
    case 'elevated': return 'Review recommended';
    case 'high_risk': return 'High risk';
    default: return riskLevel.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
}

interface MetadataPanelProps {
  session: Session | null;
  loading: boolean;
  error: string | null;
  reportedIP?: string | null;
  isUnlocked?: boolean;
}

const MetadataPanel: React.FC<MetadataPanelProps> = ({ session, loading, error, reportedIP, isUnlocked = false }) => {
  if (loading) {
    return (
      <div className="metadata-panel">
        <div className="metadata-loading">
          Loading session metadata...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metadata-panel">
        <div className="metadata-error">
          Error loading session: {error}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="metadata-panel">
        <div className="metadata-error">
          No session data available
        </div>
      </div>
    );
  }

  if (!isUnlocked) {
    const groups = [
      ['Session Overview', ['Created', 'Expires', 'URL', 'Claim Mode', 'Declared Intent', 'Verified Intent', 'Lead Quality']],
      ['Browser Information', ['User Agent', 'Platform', 'Screen Dimensions']],
      ['Client Information', ['Reported IP', 'Referrer', 'Location']],
    ] as const;
    return <div className="metadata-panel"><div className="metadata-grid">
      {groups.map(([title, fields], i) => <div className="metadata-section" key={title}>
        <h3>{title}</h3>
        {i === 0 && <div className="metadata-item"><span className="metadata-label">Verfi ID:</span><span className="metadata-value">{session.verfiID}</span></div>}
        {fields.map(label => <div className="metadata-item" key={label}><span className="metadata-label">{label}:</span><span className="metadata-value"><LockedBadge /></span></div>)}
      </div>)}
    </div></div>;
  }

  return (
    <div className="metadata-panel">
      <div className="metadata-grid">
        <div className="metadata-section">
          <h3>Session Overview</h3>
          <div className="metadata-item">
            <span className="metadata-label">Verfi ID:</span>
            <span className="metadata-value">{session.verfiID}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Created:</span>
            <span className="metadata-value">{session.createdAt ? new Date(session.createdAt).toLocaleString() : session.metadata?.timestamp ? new Date(session.metadata.timestamp).toLocaleString() : new Date(session.startTime).toLocaleString()}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Expires:</span>
            <span className="metadata-value">{session.expirationDate ? new Date(session.expirationDate).toLocaleString() : 'Not available'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">URL:</span>
            <span className="metadata-value">{session.metadata?.url || 'Not available'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Claim Mode:</span>
            <span className="metadata-value">
              <span className={`claim-mode-badge claim-mode-badge--${session.claimMode ?? 'multi'}`}>
                {session.claimMode === 'exclusive' ? 'Exclusive (single-claim)' : 'Multi-claim'}
              </span>
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Declared Intent:</span>
            <span className="metadata-value">
              {session.intent?.verticals && session.intent.verticals.length > 0 ? (
                <span>
                  {session.intent.verticals.map(v => (
                    <span key={v} className="intent-badge">{humanizeVertical(v)}</span>
                  ))}
                  {session.intent.products && session.intent.products.length > 0 && (
                    <span className="intent-products">{session.intent.products.join(', ')}</span>
                  )}
                </span>
              ) : (
                'Not declared'
              )}
            </span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Verified Intent:</span>
            <span className="metadata-value">
              {session.intent?.verifiedAt ? (
                <span>
                  <span className={`verified-score ${session.intent.verifiedScore !== undefined && session.intent.verifiedScore >= 0.6 ? 'verified-score--high' : 'verified-score--low'}`}>
                    {session.intent.verifiedScore !== undefined
                      ? `${Math.round(session.intent.verifiedScore * 100)}% match`
                      : 'n/a'}
                  </span>
                  {/*
                    Show verified intent as confirmation of the DECLARED vertical(s)
                    only — the badge answers "is this lead really what the publisher
                    said it is?". Rendering the full verifiedVerticals list here was
                    misleading: that list includes every starter vertical whose
                    keywords incidentally appeared in the recording (e.g. a car-
                    insurance form trips 'coverage'/'insure'/'zip' → life/home), which
                    reads like cross-category selling — the opposite of our pitch.
                    Intersect declared ∩ verified so we badge only the declared
                    vertical(s) that the content actually confirmed.
                  */}
                  {(() => {
                    const declared = session.intent.verticals ?? [];
                    const verified = session.intent.verifiedVerticals ?? [];
                    const confirmed = declared.filter(v => verified.includes(v));
                    return confirmed.length > 0 ? (
                      <span>
                        {confirmed.map(v => (
                          <span key={v} className="intent-badge">{humanizeVertical(v)}</span>
                        ))}
                      </span>
                    ) : null;
                  })()}
                </span>
              ) : (
                <span
                  className="verified-score--pending"
                  title="Verified intent is computed only when a buyer claims this session with a requireVerifiedScore gate. A plain claim, or a claim that only uses requireVerticals, does not trigger it. No verified-intent claim has been made yet."
                >
                  Not yet verified — runs when a buyer claims with a verified-intent requirement
                </span>
              )}
            </span>
          </div>
          {typeof session.qualitySignals?.score === 'number' && (
            <div className="metadata-item">
              <span className="metadata-label">Lead Quality:</span>
              <span className="metadata-value">
                <span className={`quality-score ${session.qualitySignals.score >= 51 ? 'quality-score--high' : 'quality-score--low'}`}>
                  {session.qualitySignals.score} · {humanizeRiskLevel(session.qualitySignals.riskLevel)}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="metadata-section">
          <h3>Browser Information</h3>
          <div className="metadata-item">
            <span className="metadata-label">User Agent:</span>
            <span className="metadata-value">{session.browserInfo?.userAgent || session.userAgent || 'Not available'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Platform:</span>
            <span className="metadata-value">{session.browserInfo?.platform || session.os?.name || 'Not available'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Screen Dimensions:</span>
            <span className="metadata-value">
              {session.browserInfo?.screenWidth && session.browserInfo?.screenHeight 
                ? `${session.browserInfo.screenWidth} x ${session.browserInfo.screenHeight}` 
                : 'Not available'}
            </span>
          </div>
        </div>

        <div className="metadata-section">
          <h3>Client Information</h3>
          <div className="metadata-item">
            <span className="metadata-label">Reported IP:</span>
            <span className="metadata-value">{reportedIP !== undefined ? (reportedIP || 'Not available') : 'Locked'}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Referrer:</span>
            <span className="metadata-value">{session.browserInfo?.referrer || 'Not available'}</span>
          </div>
          {session.geo && (
            <div className="metadata-item">
              <span className="metadata-label">{session.networkSource === 'trusted-proxy' ? 'Approximate network location:' : 'Reported location (unverified):'}</span>
              <div className="metadata-value">
                <LocationMap
                  geo={session.geo}
                  width={180}
                  height={100}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetadataPanel;
